import { useEffect, useState, useCallback } from "react";
import { Send, MailCheck, Eye, MessageCircle, Phone, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loading, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

const STATUS_CLS = {
  既読: "bg-green-100 text-green-800",
  未読: "bg-slate-100 text-slate-600",
  送信済: "bg-blue-100 text-blue-800",
  配信失敗: "bg-rose-100 text-rose-800",
};
const CHANNEL_LABEL = { inapp: "サイト内メール", line: "LINE", whatsapp: "WhatsApp" };

export default function DeliveryReport() {
  const [batches, setBatches] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get("/delivery-report").then(({ data }) => setBatches(data)), []);
  useEffect(() => { load(); }, [load]);

  const resend = async (batchId) => {
    setBusy(batchId);
    try {
      const { data } = await api.post(`/delivery-report/${batchId}/resend`);
      if (data.sent > 0) toast.success(`再送しました（成功 ${data.sent}件 / 失敗 ${data.still_failed}件）`);
      else toast.warning(`再送を試みました（成功 0件 / 失敗 ${data.still_failed}件）。宛先の連携状況をご確認ください`);
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  if (!batches) return <Loading />;

  return (
    <div>
      <PageHeader title="配信レポート" subtitle="通知の配信状況・開封（既読）状況・配信失敗を確認できます" />
      {batches.length ? (
        <div className="space-y-3">
          {batches.map((b) => {
            const rate = b.inapp ? Math.round((b.read / b.inapp) * 100) : 0;
            const hasFail = (b.failed || 0) > 0;
            return (
              <div key={b.batch_id} className={`bg-white rounded-xl border p-4 sm:p-5 ${hasFail ? "border-rose-200" : "border-slate-200"}`} data-testid={`batch-${b.batch_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{b.category}</span>
                      <span className="text-xs text-slate-400">{(b.created_at || "").slice(0, 16).replace("T", " ")}</span>
                      {hasFail && (
                        <span className="inline-flex items-center gap-1 text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-medium" data-testid={`batch-followup-${b.batch_id}`}>
                          <AlertTriangle className="w-3 h-3" /> 要フォロー {b.failed}件
                        </span>
                      )}
                    </div>
                    <div className="font-medium text-slate-800 mt-1.5">{b.subject}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasFail && (
                      <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={busy === b.batch_id} onClick={() => resend(b.batch_id)} data-testid={`batch-resend-${b.batch_id}`}>
                        {busy === b.batch_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" />失敗分を再送</>}
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setDetail(b)} data-testid={`batch-detail-${b.batch_id}`}>宛先の詳細</Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-4">
                  <Metric icon={Send} label="配信対象" value={`${b.total}名`} tone="slate" />
                  <Metric icon={Eye} label="開封（既読）" value={`${b.read} / ${b.inapp}`} sub={`開封率 ${rate}%`} tone="blue" />
                  <Metric icon={MessageCircle} label="LINE送信" value={`${b.line_sent}件`} tone="green" />
                  <Metric icon={Phone} label="WhatsApp送信" value={`${b.whatsapp_sent}件`} tone="green" />
                  <Metric icon={AlertTriangle} label="配信失敗" value={`${b.failed || 0}件`} tone={hasFail ? "rose" : "slate"} />
                </div>
              </div>
            );
          })}
        </div>
      ) : <EmptyState title="配信履歴がありません" hint="職員周知やリマインドを配信すると、ここに記録されます" />}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.subject}</DialogTitle></DialogHeader>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>宛先</TableHead><TableHead>チャネル</TableHead><TableHead>状態</TableHead><TableHead>開封時刻 / 失敗理由</TableHead></TableRow></TableHeader>
              <TableBody>
                {(detail?.recipients || []).map((r, i) => (
                  <TableRow key={i} data-testid={`recipient-${i}`} className={r.failed ? "bg-rose-50" : ""}>
                    <TableCell className="font-medium text-slate-700">{r.name || "-"}</TableCell>
                    <TableCell className="text-slate-500">{CHANNEL_LABEL[r.channel] || r.channel}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${STATUS_CLS[r.status] || "bg-slate-100 text-slate-600"}`}>
                        {r.status === "既読" && <MailCheck className="w-3 h-3" />}
                        {r.status === "配信失敗" && <AlertTriangle className="w-3 h-3" />}
                        {r.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-400">
                      {r.failed ? <span className="text-rose-600" data-testid={`recipient-reason-${i}`}>{r.detail || "配信エラー（送信先を確認してください）"}</span>
                        : (r.read_at ? r.read_at.slice(0, 16).replace("T", " ") : "-")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, tone }) {
  const tones = { slate: "text-slate-500 bg-slate-100", blue: "text-blue-600 bg-blue-50", green: "text-green-600 bg-green-50", rose: "text-rose-600 bg-rose-50" };
  return (
    <div className={`rounded-lg border p-3 ${tone === "rose" ? "bg-rose-50 border-rose-100" : "bg-slate-50 border-slate-100"}`}>
      <div className="flex items-center gap-1.5 text-xs text-slate-500"><span className={`p-1 rounded ${tones[tone]}`}><Icon className="w-3.5 h-3.5" /></span>{label}</div>
      <div className={`mt-1.5 font-semibold ${tone === "rose" ? "text-rose-700" : "text-slate-800"}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
    </div>
  );
}
